/**
 * Client error monitoring: logs to console + optional PostHog event (consent-gated).
 * On runtime errors (e.g. “Cannot convert object to primitive value”), captures
 * message, stack, route, and source without PII/tokens.
 */

import { capturePostHogEvent } from "./posthog.js";
import { sentryCaptureException } from "./sentry.js";
import { toErrorDisplayMessage } from "./utils.js";

export type ClientErrorSource =
  | "error_boundary"
  | "window_error"
  | "unhandledrejection"
  | "bootstrap";

const LOG_TAG = "[TakeLead:client-error]";
const MAX_FIELD_LEN = 8000;
const DEDUPE_MS = 4000;

let monitorInstalled = false;
let lastFingerprint = "";
let lastReportAt = 0;

function truncate(s: string): string {
  if (s.length <= MAX_FIELD_LEN) return s;
  return `${s.slice(0, MAX_FIELD_LEN)}…`;
}

function fingerprint(message: string, stack: string): string {
  return `${message.slice(0, 240)}|${stack.slice(0, 240)}`;
}

function shouldSkipDuplicate(fp: string): boolean {
  const now = Date.now();
  if (fp === lastFingerprint && now - lastReportAt < DEDUPE_MS) return true;
  lastFingerprint = fp;
  lastReportAt = now;
  return false;
}

function normalizeReason(reason: unknown): { name: string; message: string; stack: string } {
  if (reason instanceof Error) {
    return {
      name: reason.name,
      message: reason.message,
      stack: reason.stack ?? "",
    };
  }
  if (reason && typeof reason === "object" && "message" in reason) {
    const o = reason as { message?: unknown; stack?: unknown };
    return {
      name: "Error",
      message: toErrorDisplayMessage(o.message),
      stack: typeof o.stack === "string" ? o.stack : "",
    };
  }
  return {
    name: "Unknown",
    message: toErrorDisplayMessage(reason),
    stack: "",
  };
}

export type ClientErrorPayload = {
  source: ClientErrorSource;
  error_name: string;
  message: string;
  stack: string;
  pathname: string;
  component_stack?: string;
  ts: string;
};

/**
 * Report a client error: always logs via console.error; sends a PostHog `client_error` event when active.
 */
export function reportClientError(
  source: ClientErrorSource,
  reason: unknown,
  extra?: { componentStack?: string }
): void {
  const { name, message, stack } = normalizeReason(reason);
  const fp = fingerprint(message, stack);
  if (shouldSkipDuplicate(fp)) return;

  const payload: ClientErrorPayload = {
    source,
    error_name: name,
    message: truncate(message),
    stack: truncate(stack),
    pathname: typeof window !== "undefined" ? window.location.pathname : "",
    ts: new Date().toISOString(),
  };
  if (extra?.componentStack) {
    payload.component_stack = truncate(extra.componentStack);
  }

  try {
    console.error(LOG_TAG, payload);
  } catch {
    /* ignore */
  }

  try {
    const err = reason instanceof Error ? reason : new Error(message);
    sentryCaptureException(err, {
      source: payload.source,
      pathname: payload.pathname,
      ...(payload.component_stack ? { component_stack: payload.component_stack } : {}),
    });
  } catch {
    /* ignore */
  }

  try {
    capturePostHogEvent("client_error", {
      source: payload.source,
      error_name: payload.error_name,
      message: payload.message,
      stack: payload.stack,
      pathname: payload.pathname,
      ...(payload.component_stack ? { component_stack: payload.component_stack } : {}),
      ts: payload.ts,
      distinct_error: `${payload.error_name}: ${payload.message.slice(0, 160)}`,
    });
  } catch {
    /* ignore */
  }
}

/** Install global error listeners (window.error, unhandledrejection). Idempotent. */
export function installClientErrorMonitor(): void {
  if (typeof window === "undefined" || monitorInstalled) return;
  monitorInstalled = true;

  window.addEventListener("error", (event) => {
    if (event.error != null) {
      reportClientError("window_error", event.error);
      return;
    }
    const msg = event.message?.trim() || "window.error (no Error object)";
    reportClientError("window_error", new Error(msg));
  });

  window.addEventListener("unhandledrejection", (event) => {
    reportClientError("unhandledrejection", event.reason);
  });
}
